import { FiltrosEditais } from "@/types/atos-pessoal/concursos/filtros-editais";
import { Service } from "../mainService";
import { Banca } from "@/types/atos-pessoal/concursos/banca";
import { Concurso } from "@/types/atos-pessoal/concursos/concurso";
import { EditalEnvio } from "@/types/atos-pessoal/concursos/edital-envio";
import { TipoAnexo } from "@/types/atos-pessoal/concursos/tipo-anexo";
import { PortalError } from "@/types/portal-error";
import { AnexoEdital } from "@/types/atos-pessoal/concursos/anexo";
import { CargoResumido } from "@/types/atos-pessoal/cargos/cargo-resumido";
import { CargoEnvio } from "@/types/atos-pessoal/concursos/cargo-envio";
import { EditalCargo } from "@/types/atos-pessoal/concursos/edital-cargo";
import { Classificado } from "@/types/atos-pessoal/concursos/classificado";
import { ProcessoLicitatorio } from "@/types/atos-pessoal/concursos/processo-licitatorio";
import { TipoAnexoEnvio } from "@/types/atos-pessoal/concursos/tipo-anexo-envio";
import { FiltrosTipoAnexo } from "@/types/atos-pessoal/concursos/filtros-tipo-anexo";

const concursoService = {
  adicionarAnexo,
  adicionarCargosEdital,
  adicionarTipoAnexo,
  alterarTipoAnexo,
  baixarAnexo,
  cadastrarClassificado,
  cancelarConcurso,
  criarBanca,
  criarEdital,
  desfazerSituacao,
  editarBanca,
  editarEdital,
  excluirAnexo,
  excluirCargoEdital,
  excluirClassificado,
  getBancas,
  getConcursos,
  listarAnexos,
  listarCargos,
  listarCargosEdital,
  listarClassificados,
  listarTiposAnexo,
  listarTiposArquivo,
  listarTipoClassificado,
  removerBanca,
  removerEdital,
  removerTipoAnexo,
  homologarData,
  prorrogarConcurso,
  getConcursoById,
  getPendencias,
  getPendenciasHml,
  getListarLicitacoes,
  getHabilitarCodIgespLicitacao,
  baixarModeloClassificados,
  importarClassificadosPlanilha,
  getAllUgs,
};

async function getConcursos(
  currentPage: number,
  filtros: FiltrosEditais
): Promise<Concurso> {
  try {
    for (const key in filtros)
      if (filtros[key] === null || filtros[key] === undefined)
        delete filtros[key];
    const response = await Service.$http.post(`
      ${process.env.VUE_APP_PESSOAL_API
      }/Concurso/edital/filtrar?page=${currentPage}&pageSize=10${filtros.Situacao ? `&filtroSituacao=${filtros.Situacao}` : ""
      }${filtros.Tipo ? `&filtroTipo=${filtros.Tipo}` : ""}${filtros.Banca ? `&filtroBanca=${filtros.Banca}` : ""
      }${filtros.NrEdital ? `&filtroNumero=${filtros.NrEdital}` : ""}${filtros.AnoEdital ? `&filtroAno=${filtros.AnoEdital}` : ""
      }${filtros.UGResponsavel ? `&filtroUgResponsavel=${filtros.UGResponsavel}` : ""
      }
    `);
    return response.data;
  } catch (error: any) {
    console.error(error);
    return {
      page: 0,
      pageSize: 0,
      totalRows: 0,
      editais: [],
    };
  }
}

async function getBancas(): Promise<Banca[]> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_PESSOAL_API}/Banca/listar`
    );
    return response.data;
  } catch (error: any) {
    console.error(error);
    return [];
  }
}

async function criarEdital(data: EditalEnvio) {
  try {
    const response = await Service.$http.post(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/edital`,
      data
    );
    if (response && response.status >= 200 && response.status < 300)
      return response.data;
    throw new PortalError(
      response.data?.erros || "Não foi possível criar o edital"
    );
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }

}

async function editarEdital(data: EditalEnvio, idEdital: number) {
  try {
    const response = await Service.$http.patch(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/${idEdital}`,
      data
    );
    if (response && response.status >= 200 && response.status < 300)
      return response.data;
    throw new PortalError(
      response.data?.erros || "Não foi possível editar o edital"
    );
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function removerBanca(id: number): Promise<void | string> {
  try {
    const response = await Service.$http.delete(
      `${process.env.VUE_APP_PESSOAL_API}/Banca/remover/${id}`
    );
    if (response.status > 200 && response.status < 300) return;
  } catch (error: any) {
    console.error(error);
    return error.response.data.erros[0];
  }
}

async function editarBanca(banca: Banca): Promise<void | string> {
  try {
    await Service.$http.patch(
      `${process.env.VUE_APP_PESSOAL_API}/Banca/alterar/${banca.IdBanca}`,
      banca
    );
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function criarBanca(banca: Banca): Promise<any> {
  try {
    const response = await Service.$http.post(
      `${process.env.VUE_APP_PESSOAL_API}/Banca/adicionar`,
      banca
    );
    return response.data;
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function listarTiposArquivo(tpEdital: 'CON' | 'PSS' | 'PSP', tpGrupoAnexo?: 'CAD' | 'PUB' | 'HML') {
  const qs = new URLSearchParams()
  qs.set('tpEdital', tpEdital);
  if (tpGrupoAnexo) qs.set('tpGrupoAnexo', tpGrupoAnexo);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  const response = await Service.$http.get(
    `${process.env.VUE_APP_PESSOAL_API}/Concurso/tipoAnexo/listar${query}`
  );
  if (response && response.status === 200) {
    return response.data.map((ta: any) => new TipoAnexo(ta));
  }
  throw new PortalError("Erro ao listar tipos de arquivo");
}

async function adicionarAnexo(anexo) {
  try {
    const data = new FormData();
    data.append("idEdital", String(anexo.idEdital));
    data.append("tipoAnexo", String(anexo.tipoAnexo));
    data.append("Anexo", anexo.Anexo as unknown as Blob);
    if (anexo.dsDetalhes) data.append("dsDetalhes", anexo.dsDetalhes);

    const response = await Service.$http.post(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/anexo/adicionar`,
      data
    );
    if (response && response.status === 200) {
      return response;
    } else {
      throw new PortalError(
        response.data?.erros || "Ocorreu um erro ao adicionar o anexo."
      );
    }
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function baixarAnexo(idAnexo: number) {
  try {
    const response = await Service.$http.get(`${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/anexo/${idAnexo}`, { responseType: "blob" });
    if (response && response.status === 200) return response
    else {
      throw new PortalError(
        response.data?.erros || "Ocorreu um erro fazer download do anexo."
      );
    }
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function listarAnexos(idEdital: number): Promise<AnexoEdital[]> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/${idEdital}/anexos`
  );
  if (response && response.status === 200) {
    const lista = response.data.map((a: any) => new AnexoEdital(a));
    return lista;
  }
  throw new PortalError("Erro ao listar Anexos");
}

async function excluirAnexo(idAnexo: number) {
  try {
    const response = await Service.$http.delete(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/anexo/${idAnexo}`
    );
    if (response.status > 200 && response.status < 300) return response;
    else {
      throw new PortalError(
        response.data?.erros || "Ocorreu um erro excluir o anexo."
      );
    }
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    }

  }
}

async function listarCargos() {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_PESSOAL_API}/Concurso/cargosEfetivos`
  );

  if (response && response.status === 200)
    return response.data.map((ca: any) => new CargoResumido(ca));

  throw new PortalError("Não foi possível listar os cargos");
}

async function adicionarCargosEdital(idEdital: number, data: CargoEnvio) {
  try {
    data.idCargo = data.idCargo === 0 ? null : data.idCargo;
    if (data.remuneracaoInicial) data.remuneracaoInicial = String(data.remuneracaoInicial)
    if (data.vlInscricaoAmpla) data.vlInscricaoAmpla = String(data.vlInscricaoAmpla)
    const response = await Service.$http.post(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/${idEdital}/cargo/adicionar`,
      data
    );
    if (response && response.status >= 200 && response.status <= 300) {
      return response.data
    } else {
      throw new PortalError(
        response.data?.erros || "Ocorreu um erro ao adicionar o cargo ao edital."
      );
    }
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors)
        for (const [key, value] of Object.entries(e.response.data.errors))
          errors.push(`${key}: ${value}`);
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function listarCargosEdital(idEdital: number) {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/${idEdital}/cargos`
    );
    if (response && response.status === 200) {
      return response.data.map((ca: any) => new EditalCargo(ca));
    } else {
      throw new PortalError(
        response.data?.erros || "Ocorreu um erro ao listar os cargos do edital."
      );
    }
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function excluirCargoEdital(idEditalCargo: number) {
  const response = await Service.$http.delete(
    `${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/cargo/remover/${idEditalCargo}`
  );
  if (response && response.status === 204) return response;
  throw new PortalError(
    `Não foi possível excluir o cargo de id ${idEditalCargo}`
  );
}

async function removerEdital(idEdital: number) {
  try {
    const response = await Service.$http.delete(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/${idEdital}`
    );
    if (response && response.status >= 200 && response.status < 300)
      return response;
    throw new PortalError(
      response.data?.erros || "Não foi possível excluir o concurso"
    );
  } catch (e: any) {
    if (e.response) {
      const errors: any = [];
      if (e.response.data.errors)
        for (const [key, value] of Object.entries(e.response.data.errors))
          errors.push(`${key}: ${value}`);
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else throw e;
  }
}

async function homologarData(idEdital: number, data: Date | string | null) {
  const dataEnvio = {
    dtHomologacao: data
  }

  try {

    const response = await Service.$http.patch(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/${idEdital}/dtHomologacao`, dataEnvio
    );
    if (response && response.status >= 200 && response.status < 300)
      return response;
    throw new PortalError(
      response.data?.erros || "Não foi possível adicionar a data de homologação."
    );
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function cancelarConcurso(payload: any) {
  try {
    const data = new FormData();
    data.append("DtCancelamento", String(payload.DtCancelamento));
    data.append(
      "JustificativaCancelamento",
      payload.JustificativaCancelamento as unknown as Blob
    );
    const response = await Service.$http.patch(
      `
    ${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/${payload.IdEdital}/cancelar`,
      data
    );
    if (response && response.status >= 200 && response.status < 300)
      return response;
    throw new PortalError(
      response.data?.erros || "Não foi possível cancelar o concurso"
    );
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function prorrogarConcurso(IdEdital: number, anexo: any) {
  try {
    const data = new FormData();
    data.append("AtoProrrogacao", anexo as unknown as Blob);
    const response = await Service.$http.patch(
      `
    ${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/${IdEdital}/prorrogar`,
      data
    );
    if (response && response.status >= 200 && response.status < 300)
      return response;
    throw new PortalError(
      response.data?.erros || "Não foi possível prorrogar o concurso"
    );
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

//Classificados

async function desfazerSituacao(
  idEdital: number,
  _situacao: string,
  prorrogado: boolean
) {
  try {
    const situacao = prorrogado
      ? "prorrogacao"
      : _situacao === "Homologado"
        ? "homologacao"
        : "cancelamento";
    const response = await Service.$http.patch(`
    ${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/${idEdital}/desfazerAcao?acao=${situacao}`);
    if (response && response.status >= 200 && response.status < 300)
      return response;
    throw new PortalError(
      response.data?.erros || "Não foi possível desfazer a situação do concurso"
    );
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

//Classificados
async function listarTipoClassificado() {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/tipoClassificado/listar`
    );
    if (response && response.status === 200) {
      return response.data;
    } else {
      throw new PortalError(
        response.data?.erros ||
        "Ocorreu um erro ao listar os tipos de classificado"
      );
    }
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function listarClassificados(idEdital: number) {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/${idEdital}/classificados`
    );
    if (response && response.status === 200) {
      return response.data.map((ca: any) => new EditalCargo(ca));
    } else {
      throw new PortalError(
        response.data?.erros || "Ocorreu um erro ao listar os classificados"
      );
    }
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function cadastrarClassificado(idEdital: number, data: Classificado) {
  try {
    const response = await Service.$http.post(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/${idEdital}/classificado/adicionar`,
      data
    );
    if (response && response.status === 200) {
      return response;
    } else {
      throw new PortalError(
        response.data?.erros || "Ocorreu um erro ao adicionar o classificado."
      );
    }
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function excluirClassificado(idClassificado: number) {
  try {
    const response = await Service.$http.delete(`${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/classificado/remover/${idClassificado}`)
    if (response && response.status === 204) {
      return response;
    } else {
      throw new PortalError(
        response.data?.erros || "Ocorreu um erro ao excluir o classificado."
      );
    }
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function getConcursoById(idEdital: any): Promise<any> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/${idEdital}`
    );
    return response.data;
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function getPendencias(idEdital: any): Promise<any> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/${idEdital}/pendencias`
    );
    return response.data;
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function getPendenciasHml(idEdital: any): Promise<any> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/edital/${idEdital}/pendenciasHml`
    );
    return response.data;
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function getListarLicitacoes(cnpjBanca: number): Promise<ProcessoLicitatorio[]> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/processoLicitatorio/listar/${cnpjBanca}`
    )
    return response.data
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors)
        for (const [key, value] of Object.entries(e.response.data.errors))
          errors.push(`${key}: ${value}`);
      throw new PortalError(e.response.data.erros ?? e.response.data.msg ?? errors);
    } else throw e;
  }
}


async function getHabilitarCodIgespLicitacao(): Promise<boolean> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/habilitarCodIgespLicitacao`
    );
    return response.data;
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function baixarModeloClassificados() {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/baixarModeloImportacaoClassificados`, { responseType: "blob" }
    );
    return response.data;
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function importarClassificadosPlanilha(fd: FormData) {
  try {
    const response = await Service.$http.post(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/importarClassificados`, fd)
    if (response && response.status === 200) {
      return response;
    } else {
      throw new PortalError(
        response.data?.erros || "Ocorreu um erro ao anexar os classificados."
      );
    }
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors) {
        for (const [key, value] of Object.entries(e.response.data.errors)) {
          errors.push(`${key}: ${value}`);
        }
      }
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else {
      throw e;
    }
  }
}

async function listarTiposAnexo(page: number, filtros: FiltrosTipoAnexo): Promise<any> {
  try {
    const params = new URLSearchParams({ page: String(page), pageSize: '10' });
    if (filtros.filtroDescricao) params.set('filtroDescricao', filtros.filtroDescricao);
    if (filtros.filtroGrupo) params.set('filtroGrupo', filtros.filtroGrupo);
    if (filtros.filtroObrigatorio !== null && filtros.filtroObrigatorio !== undefined)
      params.set('filtroObrigatorio', String(filtros.filtroObrigatorio));
    if (filtros.filtroTipoEdital && filtros.filtroTipoEdital.length > 0)
      params.set('filtroTipoEdital', filtros.filtroTipoEdital.join(';'));
    const response = await Service.$http.get(
      `${process.env.VUE_APP_PESSOAL_API}/TipoAnexo/listar?${params.toString()}`
    );
    return response.data;
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors)
        for (const [key, value] of Object.entries(e.response.data.errors))
          errors.push(`${key}: ${value}`);
      throw new PortalError(e.response.data.erros ?? e.response.data.msg ?? errors);
    }
    throw e;
  }
}

async function adicionarTipoAnexo(data: TipoAnexoEnvio): Promise<TipoAnexo> {
  try {
    const response = await Service.$http.post(
      `${process.env.VUE_APP_PESSOAL_API}/TipoAnexo/adicionar`,
      data
    );
    return response.data;
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors)
        for (const [key, value] of Object.entries(e.response.data.errors))
          errors.push(`${key}: ${value}`);
      throw new PortalError(e.response.data.erros ?? e.response.data.msg ?? errors);
    }
    throw e;
  }
}

async function alterarTipoAnexo(idTipoAnexo: number, data: TipoAnexoEnvio): Promise<TipoAnexo> {
  try {
    const response = await Service.$http.patch(
      `${process.env.VUE_APP_PESSOAL_API}/TipoAnexo/alterar/${idTipoAnexo}`,
      data
    );
    return response.data;
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors)
        for (const [key, value] of Object.entries(e.response.data.errors))
          errors.push(`${key}: ${value}`);
      throw new PortalError(e.response.data.erros ?? e.response.data.msg ?? errors);
    }
    throw e;
  }
}

async function removerTipoAnexo(idTipoAnexo: number): Promise<void> {
  try {
    await Service.$http.delete(
      `${process.env.VUE_APP_PESSOAL_API}/TipoAnexo/remover/${idTipoAnexo}`
    );
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors)
        for (const [key, value] of Object.entries(e.response.data.errors))
          errors.push(`${key}: ${value}`);
      throw new PortalError(e.response.data.erros ?? e.response.data.msg ?? errors);
    }
    throw e;
  }
}

async function getAllUgs(): Promise<{ CodUgSagres: string, NmNome: string }[]> {
  try {
    const response = await Service.$http.get(
      `${process.env.VUE_APP_PESSOAL_API}/Concurso/unidadesGestoras`
    );
    return response.data;
  } catch (e: any) {
    if (e.response && [400, 401, 403].includes(e.response.status)) {
      const errors: any = [];
      if (e.response.data.errors)
        for (const [key, value] of Object.entries(e.response.data.errors))
          errors.push(`${key}: ${value}`);
      throw new PortalError(
        e.response.data.erros ?? e.response.data.msg ?? errors
      );
    } else throw e;
  }
}

export default concursoService;
